import { useState } from 'react'
import clsx from 'clsx'
import { ForecastAnalysis } from '../../utils/api'
import { CategoryBadge } from './ForecastBadges'
import { CATEGORY_OPTIONS } from './constants'

interface ConfirmationStepProps {
  analysis: ForecastAnalysis
  onConfirm: (overrides: {
    category?: string
    target_date?: string
    topics?: string[]
    primary_entity?: string
    clarification?: string
  }) => void
  onCancel: () => void
  isSubmitting: boolean
}

export function ConfirmationStep({
  analysis,
  onConfirm,
  onCancel,
  isSubmitting,
}: ConfirmationStepProps) {
  const [editMode, setEditMode] = useState(false)
  const [editedCategory, setEditedCategory] = useState(analysis.category)
  const [editedEntity, setEditedEntity] = useState(analysis.primary_entity || '')
  const [editedDate, setEditedDate] = useState(analysis.target_date || '')
  const [editedTopics, setEditedTopics] = useState(analysis.suggested_topics.join(', '))
  const [clarification, setClarification] = useState('')

  // Separate real ambiguities from discovered facts (Notes)
  const realAmbiguities = analysis.ambiguities.filter((a) => !a.startsWith('Note:'))
  const discoveredFacts = analysis.ambiguities.filter((a) => a.startsWith('Note:'))
  const hasAmbiguities = realAmbiguities.length > 0
  const needsClarification = hasAmbiguities && !clarification.trim()

  const handleConfirm = () => {
    onConfirm({
      category: editedCategory !== analysis.category ? editedCategory : undefined,
      target_date: editedDate !== analysis.target_date ? editedDate : undefined,
      topics:
        editedTopics !== analysis.suggested_topics.join(', ')
          ? editedTopics
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : undefined,
      primary_entity: editedEntity !== analysis.primary_entity ? editedEntity : undefined,
      clarification: clarification.trim() || undefined,
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-mono text-star">Confirm Question Details</h3>
        <button
          onClick={() => setEditMode(!editMode)}
          className="text-sm text-signal-blue hover:text-signal-blue/80"
        >
          {editMode ? 'Done Editing' : 'Edit'}
        </button>
      </div>

      {/* Ambiguity Clarification Section - Prominent at top */}
      {hasAmbiguities && (
        <div className="p-4 bg-signal-amber/10 border-2 border-signal-amber/40 rounded-lg space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">⚠️</span>
            <h4 className="font-mono text-signal-amber font-medium">Clarification Needed</h4>
          </div>
          <p className="text-sm text-star-muted">
            The question has some ambiguities. Please clarify to get better results:
          </p>
          <ul className="space-y-1 mb-3">
            {realAmbiguities.map((amb, i) => (
              <li key={i} className="text-sm text-star flex items-start gap-2">
                <span className="text-signal-amber">•</span>
                {amb}
              </li>
            ))}
          </ul>
          <textarea
            value={clarification}
            onChange={(e) => setClarification(e.target.value)}
            placeholder="e.g., 'I mean which political party will win, not which leader'"
            className="w-full px-3 py-2 bg-void border border-signal-amber/30 rounded text-star text-sm placeholder:text-star-dim/50 focus:outline-none focus:border-signal-amber/60 min-h-[80px]"
          />
          {clarification.trim() && (
            <div className="flex items-center gap-2 text-xs text-signal-green">
              <span>✓</span> Clarification provided
            </div>
          )}
        </div>
      )}

      {/* Discovered Facts (Notes) */}
      {discoveredFacts.length > 0 && (
        <div className="p-3 bg-signal-blue/10 border border-signal-blue/30 rounded-lg">
          <h4 className="text-sm font-mono text-signal-blue mb-2">📋 Discovered Facts</h4>
          <ul className="space-y-1">
            {discoveredFacts.map((fact, i) => (
              <li key={i} className="text-sm text-star-muted">
                {fact.replace('Note: ', '')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Question */}
      <div className="p-4 bg-void-50 rounded-lg space-y-4">
        <div>
          <label className="block text-xs text-star-dim mb-1">Normalized Question</label>
          <p className="text-star">{analysis.normalized_question}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-star-dim mb-1">Category</label>
            {editMode ? (
              <select
                value={editedCategory}
                onChange={(e) => setEditedCategory(e.target.value)}
                className="w-full px-3 py-2 bg-void border border-star-dim/20 rounded text-star text-sm"
              >
                {CATEGORY_OPTIONS.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            ) : (
              <CategoryBadge category={editedCategory} />
            )}
          </div>

          <div>
            <label className="block text-xs text-star-dim mb-1">Type</label>
            <span className="text-star text-sm">{analysis.question_type}</span>
          </div>

          <div>
            <label className="block text-xs text-star-dim mb-1">Primary Entity</label>
            {editMode ? (
              <input
                type="text"
                value={editedEntity}
                onChange={(e) => setEditedEntity(e.target.value)}
                className="w-full px-3 py-2 bg-void border border-star-dim/20 rounded text-star text-sm"
                placeholder="e.g., Bitcoin, Labour Party"
              />
            ) : (
              <span className="text-star text-sm">{editedEntity || '-'}</span>
            )}
          </div>

          <div>
            <label className="block text-xs text-star-dim mb-1">Target Date</label>
            {editMode ? (
              <input
                type="text"
                value={editedDate}
                onChange={(e) => setEditedDate(e.target.value)}
                className="w-full px-3 py-2 bg-void border border-star-dim/20 rounded text-star text-sm"
                placeholder="e.g., 2026-11-28"
              />
            ) : (
              <span className="text-star text-sm">{editedDate || '-'}</span>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs text-star-dim mb-1">Topics</label>
          {editMode ? (
            <input
              type="text"
              value={editedTopics}
              onChange={(e) => setEditedTopics(e.target.value)}
              className="w-full px-3 py-2 bg-void border border-star-dim/20 rounded text-star text-sm"
              placeholder="e.g., australia, elections, politics"
            />
          ) : (
            <div className="flex flex-wrap gap-1">
              {editedTopics
                .split(',')
                .filter((t) => t.trim())
                .map((topic, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-signal-blue/10 text-signal-blue text-xs rounded"
                  >
                    {topic.trim()}
                  </span>
                ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs text-star-dim mb-1">Resolution Criteria</label>
          <p className="text-star-muted text-sm">{analysis.resolution_criteria}</p>
        </div>
      </div>

      {/* Confidence */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-star-dim">Analysis Confidence:</span>
        <span
          className={clsx(
            'font-mono',
            analysis.analysis_confidence >= 0.7
              ? 'text-signal-green'
              : analysis.analysis_confidence >= 0.4
                ? 'text-signal-amber'
                : 'text-signal-red'
          )}
        >
          {(analysis.analysis_confidence * 100).toFixed(0)}%
        </span>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-4 border-t border-star-dim/10">
        <button
          onClick={handleConfirm}
          disabled={isSubmitting}
          className={clsx(
            'flex-1 px-6 py-3 rounded-lg font-medium transition-all',
            isSubmitting
              ? 'bg-star-dim/20 text-star-dim cursor-not-allowed'
              : needsClarification
                ? 'bg-signal-amber text-void hover:bg-signal-amber/80'
                : 'bg-signal-blue text-white hover:bg-signal-blue/80'
          )}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Starting...
            </span>
          ) : needsClarification ? (
            '⚠️ Run Anyway (ambiguous)'
          ) : (
            '✓ Confirm & Run Forecast'
          )}
        </button>
        <button
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-6 py-3 rounded-lg font-medium text-star-muted hover:text-star transition-all"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
